// Centralized API client for the Service Titan clone backend.
import axios from "axios";

const BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// On any 401, clear local user cache and redirect to /login.
// Also: after any AI-charging request, the backend signals the company's
// post-usage balance via response headers when it drops at/below the
// console-configured popup threshold — surface that as a global event so
// the low-credits popup can auto-open regardless of which feature was used.
let _lowCreditsNotifiedAt = 0;
apiClient.interceptors.response.use(
  (resp) => {
    const bal = resp.headers?.["x-ai-credits-balance"];
    if (bal != null && typeof window !== "undefined") {
      const now = Date.now();
      if (now - _lowCreditsNotifiedAt > 3000) {
        _lowCreditsNotifiedAt = now;
        window.dispatchEvent(new CustomEvent("low-credits-popup", { detail: { balance: Number(bal) } }));
      }
    }
    return resp;
  },
  (err) => {
    if (err?.response?.status === 401 && typeof window !== "undefined") {
      const path = window.location.pathname;
      if (!path.startsWith("/login") && !path.startsWith("/register") && !path.startsWith("/forgot-password") && !path.startsWith("/reset-password") && !path.startsWith("/pay") && !path.startsWith("/affiliate")) {
        window.dispatchEvent(new CustomEvent("auth-expired"));
      }
    }
    return Promise.reject(err);
  },
);

// ----- Auth -----
export const authApi = {
  register: (data) => apiClient.post("/auth/register", data).then((r) => r.data),
  login: (data) => apiClient.post("/auth/login", data).then((r) => r.data),
  logout: () => apiClient.post("/auth/logout").then((r) => r.data),
  me: () => apiClient.get("/auth/me").then((r) => r.data),
  refresh: () => apiClient.post("/auth/refresh").then((r) => r.data),
  forgotPassword: (email) => apiClient.post("/auth/forgot-password", { email }).then((r) => r.data),
  resetPassword: (token, newPassword) =>
    apiClient.post("/auth/reset-password", { token, new_password: newPassword }).then((r) => r.data),
  googleSession: (sessionId, inviteToken, companyName) =>
    apiClient
      .post("/auth/google/session", {
        session_id: sessionId,
        invite_token: inviteToken || null,
        company_name: companyName || null,
      })
      .then((r) => r.data),
  googlePrefill: (sessionId) =>
    apiClient.post("/auth/google/prefill", { session_id: sessionId }).then((r) => r.data),
};

// ----- Customers -----
export const customersApi = {
  list: () => apiClient.get("/customers").then((r) => r.data),
  get: (id) => apiClient.get(`/customers/${id}`).then((r) => r.data),
  create: (data) => apiClient.post("/customers", data).then((r) => {
    window.dispatchEvent(new CustomEvent("pipeline-refresh", { detail: { reason: "customer_created" } }));
    return r.data;
  }),
  update: (id, patch) => apiClient.patch(`/customers/${id}`, patch).then((r) => r.data),
  remove: (id) => apiClient.delete(`/customers/${id}`).then((r) => r.data),
  addNote: (id, text) => apiClient.post(`/customers/${id}/notes`, { text }).then((r) => r.data),
  updateNote: (id, noteId, text) =>
    apiClient.patch(`/customers/${id}/notes/${noteId}`, { text }).then((r) => r.data),
  removeNote: (id, noteId) =>
    apiClient.delete(`/customers/${id}/notes/${noteId}`).then((r) => r.data),
  // ----- Photos (object storage) -----
  uploadPhotos: (id, files, { caption = "", source = "" } = {}) => {
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    if (caption) fd.append("caption", caption);
    if (source) fd.append("source", source);
    return apiClient
      .post(`/customers/${id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
  deletePhoto: (id, photoId) => apiClient.delete(`/customers/${id}/photos/${photoId}`).then((r) => r.data),
  updatePhotoCaption: (id, photoId, caption) =>
    apiClient.patch(`/customers/${id}/photos/${photoId}`, { caption }).then((r) => r.data),
};

export const searchApi = {
  global: (q) => apiClient.get("/search", { params: { q } }).then((r) => r.data),
};

// Build a full, cookie-authenticated <img src> for a stored photo (customer or job).
export const fileUrl = (relativeUrl) =>
  relativeUrl ? `${process.env.REACT_APP_BACKEND_URL}${relativeUrl}` : "";

// ----- CSV Import Wizard (Customers & Leads) — admin only -----
export const importsApi = {
  fields: (entity) => apiClient.get(`/imports/fields`, { params: { entity } }).then((r) => r.data),
  parse: (file, entity) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("entity", entity);
    return apiClient
      .post(`/imports/parse`, fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
  analyze: (payload) => apiClient.post(`/imports/analyze`, payload).then((r) => r.data),
  commit: (payload) => apiClient.post(`/imports/commit`, payload).then((r) => r.data),
  cancel: (importId) => apiClient.delete(`/imports/${importId}`).then((r) => r.data),
};

// ----- Technicians -----
export const techniciansApi = {  list: () => apiClient.get("/technicians").then((r) => r.data),
  get: (id) => apiClient.get(`/technicians/${id}`).then((r) => r.data),
  update: (id, patch) => apiClient.patch(`/technicians/${id}`, patch).then((r) => r.data),
  remove: (id) => apiClient.delete(`/technicians/${id}`).then((r) => r.data),
  updateLocation: (id, loc) => apiClient.post(`/technicians/${id}/location`, loc).then((r) => r.data),
  uploadAvatar: (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiClient.post(`/technicians/${id}/avatar`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  uploadDocument: (id, file, title) => {
    const fd = new FormData();
    fd.append("file", file);
    if (title) fd.append("title", title);
    return apiClient.post(`/technicians/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  deleteDocument: (id, docId) => apiClient.delete(`/technicians/${id}/documents/${docId}`).then((r) => r.data),
  // Absolute URLs for <img>/view links (cookie-authenticated, same-origin).
  fileBase: process.env.REACT_APP_BACKEND_URL,
  avatarUrl: (tech) => (tech?.avatarUrl ? `${process.env.REACT_APP_BACKEND_URL}${tech.avatarUrl}` : null),
  documentUrl: (id, docId) => `${process.env.REACT_APP_BACKEND_URL}/api/technicians/${id}/documents/${docId}/file`,
};

// ----- Jobs -----
export const jobsApi = {
  list: () => apiClient.get("/jobs").then((r) => r.data),
  get: (id) => apiClient.get(`/jobs/${id}`).then((r) => r.data),
  create: (data) => apiClient.post("/jobs", data).then((r) => r.data),
  patch: (id, patch) => apiClient.patch(`/jobs/${id}`, patch).then((r) => r.data),
  updateSummary: (id, jobSummary) => apiClient.patch(`/jobs/${id}/summary`, { jobSummary }).then((r) => r.data),
  remove: (id) => apiClient.delete(`/jobs/${id}`).then((r) => r.data),
  updateStatus: (id, status, gps, extra = {}) => apiClient.post(`/jobs/${id}/status`, {
    status,
    lat: gps?.lat ?? null,
    lng: gps?.lng ?? null,
    source: gps?.source,
    ...extra,
  }).then((r) => {
    if (r.data?.status === "Completed") window.dispatchEvent(new CustomEvent("job-status-changed", { detail: r.data }));
    return r.data;
  }),
  geoCapture: (id, payload) => apiClient.post(`/jobs/${id}/geo-capture`, payload).then((r) => {
    if (r.data?.status === "Completed") window.dispatchEvent(new CustomEvent("job-status-changed", { detail: r.data }));
    return r.data;
  }),
  addNote: (id, note) => apiClient.post(`/jobs/${id}/notes`, { note }).then((r) => r.data),
  uploadPhotos: (id, photos) => apiClient.post(`/jobs/${id}/photos`, { photos }).then((r) => r.data),
  startNow: (id) => apiClient.post(`/jobs/${id}/start-now`).then((r) => r.data),
  scheduleLater: (id) => apiClient.post(`/jobs/${id}/schedule-later`).then((r) => r.data),
  unassignedCount: () => apiClient.get("/jobs/unassigned-count").then((r) => r.data),
  titleSuggestions: (q) => apiClient.get("/jobs/title-suggestions", { params: { q } }).then((r) => r.data),
  sendConfirmation: (id) => apiClient.post(`/jobs/${id}/send-confirmation`).then((r) => r.data),
  sendReschedule: (id) => apiClient.post(`/jobs/${id}/send-reschedule`).then((r) => r.data),
  completeWorkOrder: (id, payload) => apiClient.post(`/jobs/${id}/complete-workorder`, payload).then((r) => r.data),
  payrollRecord: (id) => apiClient.get(`/jobs/${id}/payroll-record`).then((r) => r.data),
  // Job Pipeline board (filters, archiving, follow-up)
  board: (params) => apiClient.get(`/jobs/board`, { params }).then((r) => r.data),
  boardSettings: () => apiClient.get(`/jobs/board/settings`).then((r) => r.data),
  updateBoardSettings: (patch) => apiClient.put(`/jobs/board/settings`, patch).then((r) => r.data),
  archiveNow: (id) => apiClient.post(`/jobs/${id}/board/archive`).then((r) => r.data),
  restoreToBoard: (id) => apiClient.post(`/jobs/${id}/board/restore`).then((r) => r.data),
  keepOnBoard: (id) => apiClient.post(`/jobs/${id}/board/keep`).then((r) => r.data),
  setFollowUp: (id, needsFollowUp) => apiClient.patch(`/jobs/${id}/follow-up`, { needsFollowUp }).then((r) => r.data),
  // Multi-tech assignments
  addAssignment: (id, payload) => apiClient.post(`/jobs/${id}/assignments`, payload).then((r) => r.data),
  removeAssignment: (id, techId) => apiClient.delete(`/jobs/${id}/assignments/${techId}`).then((r) => r.data),
  patchAssignment: (id, techId, payload) => apiClient.patch(`/jobs/${id}/assignments/${techId}`, payload).then((r) => r.data),
  completeMyPart: (id) => apiClient.post(`/jobs/${id}/assignments/mine/complete`).then((r) => r.data),
};

export const dispatchApi = {
  live: () => apiClient.get("/dispatch/live").then((r) => r.data),
  pushLocation: (techId, loc) => apiClient.post(`/technicians/${techId}/location`, loc).then((r) => r.data),
};

// ----- Flows -----
export const flowsApi = {
  list: () => apiClient.get("/flows").then((r) => r.data),
  get: (id) => apiClient.get(`/flows/${id}`).then((r) => r.data),
  create: (data) => apiClient.post("/flows", data).then((r) => r.data),
  update: (id, data) => apiClient.put(`/flows/${id}`, data).then((r) => r.data),
  remove: (id) => apiClient.delete(`/flows/${id}`).then((r) => r.data),
  forJob: (jobId) => apiClient.get(`/flows/for-job/${jobId}`).then((r) => r.data),
  submitAnswers: (flowId, payload) => apiClient.post(`/flows/${flowId}/answers`, payload).then((r) => r.data),
};

// ----- Dashboard -----
export const dashboardApi = {
  stats: () => apiClient.get("/dashboard/stats").then((r) => r.data),
  activity: (limit = 20) => apiClient.get(`/dashboard/activity?limit=${limit}`).then((r) => r.data),
  allActivity: (limit = 500) => apiClient.get(`/dashboard/activity/all?limit=${limit}`).then((r) => r.data),
};

// ----- Guru AI Chatbot -----
export const guruApi = {
  chat: ({ sessionId, text, imageBase64, useRag = true }) =>
    apiClient.post("/guru/chat", { sessionId, text, imageBase64, useRag }).then((r) => r.data),
  listSessions: () => apiClient.get("/guru/sessions").then((r) => r.data),
  getSession: (id) => apiClient.get(`/guru/sessions/${id}`).then((r) => r.data),
  deleteSession: (id) => apiClient.delete(`/guru/sessions/${id}`).then((r) => r.data),
  // Required on-site checklist runner (mobile job detail)
  checklistTurn: (payload) => apiClient.post("/guru/checklist/turn", payload).then((r) => r.data),
  checklistComplete: (payload) => apiClient.post("/guru/checklist/complete", payload).then((r) => r.data),
  // Tool-calling agent (Phase 2): read tools auto-run, write tools return a pendingAction to confirm.
  agent: (payload) => apiClient.post("/guru/agent", payload).then((r) => r.data),
  agentExecute: (payload) => apiClient.post("/guru/agent/execute", payload).then((r) => r.data),
};

// ----- Diagnostic Guru — Guided Mode sessions (backend-persisted) -----
export const diagnosticSessionsApi = {
  create: (payload = {}) => apiClient.post("/diagnostics/sessions", payload).then((r) => r.data),
  get: (id) => apiClient.get(`/diagnostics/${id}`).then((r) => r.data),
  startFlowchart: (id, flowchartId) => apiClient.post(`/diagnostics/${id}/flowchart/start`, { flowchartId }).then((r) => r.data),
  answer: (id, answer) => apiClient.post(`/diagnostics/${id}/flowchart/answer`, { answer }).then((r) => r.data),
  ask: (id, question) => apiClient.post(`/diagnostics/${id}/ask`, { question }).then((r) => r.data),
  chat: (id, message, opts = {}) => apiClient.post(`/diagnostics/${id}/chat`, { message, skipFlowchartSearch: !!opts.skipFlowchartSearch, origin: opts.origin, imageBase64: opts.imageBase64 }).then((r) => r.data),
  historyList: () => apiClient.get("/diagnostics/history").then((r) => r.data),
  historyGet: (id) => apiClient.get(`/diagnostics/history/${id}`).then((r) => r.data),
};

// ----- Help & Support chatbot -----
export const helpApi = {
  chat: ({ sessionId, message }) =>
    apiClient.post("/help/chat", { sessionId, message }).then((r) => r.data),
};

// ----- Manuals / RAG -----
export const manualsApi = {
  list: () => apiClient.get("/manuals").then((r) => r.data),
  upload: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiClient
      .post("/manuals/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
  remove: (id) => apiClient.delete(`/manuals/${id}`).then((r) => r.data),
  search: (query, k = 4) => apiClient.post("/manuals/search", { query, k }).then((r) => r.data),
  reindex: () => apiClient.post("/manuals/reindex").then((r) => r.data),
};

// ----- Diagnostic Knowledge Base -----
// Service-agnostic KB — taxonomy + documents + chunks + sessions.
// The `list` methods accept optional scope filters; empty = tenant-wide.
export const kbApi = {
  // Taxonomy
  categories: {
    list: () => apiClient.get("/diagnostic/categories").then((r) => r.data),
    create: (data) => apiClient.post("/diagnostic/categories", data).then((r) => r.data),
    patch: (id, data) => apiClient.patch(`/diagnostic/categories/${id}`, data).then((r) => r.data),
    remove: (id) => apiClient.delete(`/diagnostic/categories/${id}`).then((r) => r.data),
  },
  equipmentTypes: {
    list: (params = {}) => apiClient.get("/diagnostic/equipment-types", { params }).then((r) => r.data),
    create: (data) => apiClient.post("/diagnostic/equipment-types", data).then((r) => r.data),
    patch: (id, data) => apiClient.patch(`/diagnostic/equipment-types/${id}`, data).then((r) => r.data),
    remove: (id) => apiClient.delete(`/diagnostic/equipment-types/${id}`).then((r) => r.data),
  },
  manufacturers: {
    list: (params = {}) => apiClient.get("/diagnostic/manufacturers", { params }).then((r) => r.data),
    create: (data) => apiClient.post("/diagnostic/manufacturers", data).then((r) => r.data),
    patch: (id, data) => apiClient.patch(`/diagnostic/manufacturers/${id}`, data).then((r) => r.data),
    remove: (id) => apiClient.delete(`/diagnostic/manufacturers/${id}`).then((r) => r.data),
  },
  models: {
    list: (params = {}) => apiClient.get("/diagnostic/models", { params }).then((r) => r.data),
    create: (data) => apiClient.post("/diagnostic/models", data).then((r) => r.data),
    patch: (id, data) => apiClient.patch(`/diagnostic/models/${id}`, data).then((r) => r.data),
    remove: (id) => apiClient.delete(`/diagnostic/models/${id}`).then((r) => r.data),
  },
  families: {
    list: (params = {}) => apiClient.get("/diagnostic/families", { params }).then((r) => r.data),
    create: (data) => apiClient.post("/diagnostic/families", data).then((r) => r.data),
    patch: (id, data) => apiClient.patch(`/diagnostic/families/${id}`, data).then((r) => r.data),
    remove: (id) => apiClient.delete(`/diagnostic/families/${id}`).then((r) => r.data),
  },
  // Documents
  documents: {
    list: (params = {}) => apiClient.get("/diagnostic/documents", { params }).then((r) => r.data),
    detail: (id) => apiClient.get(`/diagnostic/documents/${id}`).then((r) => r.data),
    upload: (fields, file, onProgress) => {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries(fields || {}).forEach(([key, val]) => {
        if (val === undefined || val === null || val === "") return;
        fd.append(key, Array.isArray(val) ? JSON.stringify(val) : String(val));
      });
      return apiClient
        .post("/diagnostic/documents/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
          },
        })
        .then((r) => r.data);
    },
    reindex: (id) => apiClient.post(`/diagnostic/documents/${id}/reindex`).then((r) => r.data),
    patch: (id, data) => apiClient.patch(`/diagnostic/documents/${id}`, data).then((r) => r.data),
    remove: (id) => apiClient.delete(`/diagnostic/documents/${id}`).then((r) => r.data),
    downloadUrl: (id) => {
      const base = (apiClient.defaults?.baseURL || "").replace(/\/$/, "");
      return `${base}/diagnostic/documents/${id}/download`;
    },
    conflicts: (id) => apiClient.get(`/diagnostic/documents/${id}/conflicts`).then((r) => r.data),
    chunks: (id) => apiClient.get(`/diagnostic/documents/${id}/chunks`).then((r) => r.data),
  },
  // Retrieval debug
  retrieve: (params) => apiClient.get("/diagnostic/retrieve", { params }).then((r) => r.data),
  // RAG search — the reusable retrieval API. Accepts a full RetrievalContext.
  search: (context) => apiClient.post("/diagnostic/kb/search", context).then((r) => r.data),
  // View Source — return one chunk + neighbors + parent doc metadata.
  citation: (chunkId, span = 1) =>
    apiClient.get(`/diagnostic/kb/citations/${chunkId}`, { params: { span } }).then((r) => r.data),
};

// ----- Flow AI Generator -----
export const flowAiApi = {
  generate: (prompt, save = false) =>
    apiClient.post("/flow-ai/generate", { prompt, save }).then((r) => r.data),
};

// ----- Membership plans -----
export const membershipPlansApi = {
  list: () => apiClient.get("/membership-plans").then((r) => r.data),
  create: (data) => apiClient.post("/membership-plans", data).then((r) => r.data),
  update: (id, patch) => apiClient.patch(`/membership-plans/${id}`, patch).then((r) => r.data),
  remove: (id) => apiClient.delete(`/membership-plans/${id}`).then((r) => r.data),
};

// ----- Estimates & Invoices (Phase A) -----
export const estimatesApi = {
  list: () => apiClient.get("/estimates").then((r) => r.data),
  get: (id) => apiClient.get(`/estimates/${id}`).then((r) => r.data),
  create: (data) => apiClient.post("/estimates", data).then((r) => r.data),
  patch: (id, patch) => apiClient.patch(`/estimates/${id}`, patch).then((r) => r.data),
  remove: (id) => apiClient.delete(`/estimates/${id}`).then((r) => r.data),
  setStatus: (id, status) => apiClient.post(`/estimates/${id}/status`, { status }).then((r) => {
    window.dispatchEvent(new CustomEvent("estimate-status-changed", { detail: r.data }));
    return r.data;
  }),
  convertToInvoice: (id) => apiClient.post(`/estimates/${id}/convert-to-invoice`).then((r) => {
    window.dispatchEvent(new CustomEvent("estimate-status-changed", { detail: r.data }));
    return r.data;
  }),
  // Phase E — signatures + deposits
  sign: (id, payload) => apiClient.post(`/estimates/${id}/sign`, payload).then((r) => {
    window.dispatchEvent(new CustomEvent("estimate-status-changed", { detail: r.data }));
    return r.data;
  }),
  clearSignature: (id) => apiClient.delete(`/estimates/${id}/signature`).then((r) => r.data),
  addDeposit: (id, deposit) => apiClient.post(`/estimates/${id}/deposits`, deposit).then((r) => r.data),
  removeDeposit: (id, depId) => apiClient.delete(`/estimates/${id}/deposits/${depId}`).then((r) => r.data),
  charge: (id, payload) => apiClient.post(`/estimates/${id}/charge`, payload).then((r) => r.data),
  createJobFromEstimate: (id, targetStatus) =>
    apiClient.post(`/estimates/${id}/create-job`, { targetStatus }).then((r) => r.data),
  // Timeline notes — mirrored to the linked customer file
  addNote: (id, text) => apiClient.post(`/estimates/${id}/notes`, { text }).then((r) => r.data),
  updateNote: (id, noteId, text) => apiClient.patch(`/estimates/${id}/notes/${noteId}`, { text }).then((r) => r.data),
  removeNote: (id, noteId) => apiClient.delete(`/estimates/${id}/notes/${noteId}`).then((r) => r.data),
};

export const invoicesApi = {
  list: () => apiClient.get("/invoices").then((r) => r.data),
  get: (id) => apiClient.get(`/invoices/${id}`).then((r) => r.data),
  create: (data) => apiClient.post("/invoices", data).then((r) => {
    window.dispatchEvent(new CustomEvent("pipeline-refresh", { detail: { reason: "invoice_created" } }));
    return r.data;
  }),
  patch: (id, patch) => apiClient.patch(`/invoices/${id}`, patch).then((r) => r.data),
  remove: (id) => apiClient.delete(`/invoices/${id}`).then((r) => r.data),
  setStatus: (id, status) => apiClient.post(`/invoices/${id}/status`, { status }).then((r) => r.data),
  addPayment: (id, payment) => apiClient.post(`/invoices/${id}/payments`, payment).then((r) => {
    window.dispatchEvent(new CustomEvent("pipeline-refresh", { detail: { reason: "payment_received" } }));
    return r.data;
  }),
  charge: (id, payload) => apiClient.post(`/invoices/${id}/charge`, payload).then((r) => {
    window.dispatchEvent(new CustomEvent("pipeline-refresh", { detail: { reason: "payment_received" } }));
    return r.data;
  }),
  removePayment: (id, payId) => apiClient.delete(`/invoices/${id}/payments/${payId}`).then((r) => r.data),
  refundPayment: (id, payId, body) =>
    apiClient.post(`/invoices/${id}/payments/${payId}/refund`, body).then((r) => r.data),
  markReviewed: (ids, reviewed = true) => apiClient.post(`/invoices/mark-reviewed`, { ids, reviewed }).then((r) => r.data),
  // Timeline notes — mirrored to the linked customer file
  addNote: (id, text) => apiClient.post(`/invoices/${id}/notes`, { text }).then((r) => r.data),
  updateNote: (id, noteId, text) => apiClient.patch(`/invoices/${id}/notes/${noteId}`, { text }).then((r) => r.data),
  removeNote: (id, noteId) => apiClient.delete(`/invoices/${id}/notes/${noteId}`).then((r) => r.data),
};

// ----- Company-side popup announcements — the audience, never the platform admin author -----
export const popupsApi = {
  getActive: () => apiClient.get("/popups/active").then((r) => r.data),
};

// ----- Pricebook (Phase B) -----
export const pricebookApi = {
  list: (kind) => apiClient.get("/pricebook", { params: kind ? { kind } : {} }).then((r) => r.data),
  get: (id) => apiClient.get(`/pricebook/${id}`).then((r) => r.data),
  create: (data) => apiClient.post("/pricebook", data).then((r) => r.data),
  patch: (id, patch) => apiClient.patch(`/pricebook/${id}`, patch).then((r) => r.data),
  remove: (id) => apiClient.delete(`/pricebook/${id}`).then((r) => r.data),
  adjustStock: (id, delta, reason) =>
    apiClient.post(`/pricebook/${id}/adjust-stock`, { delta, reason }).then((r) => r.data),
};

// ----- Users (admin only) -----
export const usersApi = {
  list: () => apiClient.get("/users").then((r) => r.data),
  seatUsage: () => apiClient.get("/users/seat-usage").then((r) => r.data),
  create: (data) => apiClient.post("/users", data).then((r) => r.data),
  update: (id, patch) => apiClient.patch(`/users/${id}`, patch).then((r) => r.data),
  remove: (id) => apiClient.delete(`/users/${id}`).then((r) => r.data),
};

// ----- Invites (admin invite send + manage; accept is public) -----
export const invitesApi = {
  create: (data) => apiClient.post("/users/invite", data).then((r) => r.data),
  bulk: (invites) => apiClient.post("/users/invite/bulk", { invites }).then((r) => r.data),
  list: () => apiClient.get("/users/invites").then((r) => r.data),
  revoke: (id) => apiClient.delete(`/users/invites/${id}`).then((r) => r.data),
  resend: (id) => apiClient.post(`/users/invites/${id}/resend`).then((r) => r.data),
  preview: (token) => apiClient.get(`/auth/invite/${token}`).then((r) => r.data),
  accept: (token, data) => apiClient.post(`/auth/invite/${token}/accept`, data).then((r) => r.data),
};

// ----- Branding / Company settings (Phase C) -----
export const brandingApi = {
  get: () => apiClient.get("/branding").then((r) => r.data),
  update: (patch) => apiClient.put("/branding", patch).then((r) => r.data),
  uploadLogo: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiClient
      .post("/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
  removeLogo: () => apiClient.delete("/branding/logo").then((r) => r.data),
};

export const tradeTypesApi = {
  get: () => apiClient.get("/trade-types").then((r) => r.data),
  update: (trades) => apiClient.put("/trade-types", { trades }).then((r) => r.data),
};

// ----- Per-company SMTP email settings (admin-only) -----
export const emailApi = {
  getSettings: () => apiClient.get("/email/settings").then((r) => r.data),
  updateSettings: (patch) => apiClient.put("/email/settings", patch).then((r) => r.data),
  sendTest: (to) => apiClient.post("/email/test", { to }).then((r) => r.data),
  syncInboxNow: () => apiClient.post("/email/inbox-sync").then((r) => r.data),
};

// ----- Gmail OAuth (send-as) — connect/disconnect -----
export const gmailApi = {
  connectUrl: () => `${BASE_URL}/auth/gmail/connect`,
  disconnect: () => apiClient.post("/auth/gmail/disconnect").then((r) => r.data),
};

// ----- Address autocomplete (OpenStreetMap Nominatim proxy) -----
export const geoApi = {
  autocomplete: (q) =>
    apiClient.get("/geo/autocomplete", { params: { q } }).then((r) => r.data),
};

// ----- Per-company Stripe connection (admin-only) -----
export const stripeApi = {
  getSettings: () => apiClient.get("/stripe/settings").then((r) => r.data),
  updateSettings: (patch) => apiClient.put("/stripe/settings", patch).then((r) => r.data),
  testConnection: () => apiClient.post("/stripe/test").then((r) => r.data),
};

export const paymentProvidersApi = {
  catalog: () => apiClient.get("/payment-providers/catalog").then((r) => r.data),
  accounts: () => apiClient.get("/payment-providers/accounts").then((r) => r.data),
  save: (provider, payload) => apiClient.put(`/payment-providers/accounts/${provider}`, payload).then((r) => r.data),
  test: (provider) => apiClient.post(`/payment-providers/accounts/${provider}/test`).then((r) => r.data),
  setDefault: (provider) => apiClient.post(`/payment-providers/accounts/${provider}/default`).then((r) => r.data),
  disconnect: (provider) => apiClient.delete(`/payment-providers/accounts/${provider}`).then((r) => r.data),
  getBehavior: () => apiClient.get("/payment-providers/behavior").then((r) => r.data),
  saveBehavior: (payload) => apiClient.put("/payment-providers/behavior", payload).then((r) => r.data),
  getSignoffText: () => apiClient.get("/payment-providers/signoff-text").then((r) => r.data),
};

// ----- Marketing (email campaigns to your customers) -----
export const marketingApi = {
  overview: () => apiClient.get("/marketing/overview").then((r) => r.data),
  getSettings: () => apiClient.get("/marketing/settings").then((r) => r.data),
  updateSettings: (patch) => apiClient.put("/marketing/settings", patch).then((r) => r.data),
  templates: () => apiClient.get("/marketing/templates").then((r) => r.data),
  previewAudience: (criteria) => apiClient.post("/marketing/audience/preview", criteria).then((r) => r.data),
  test: (payload) => apiClient.post("/marketing/test", payload).then((r) => r.data),
  listCampaigns: () => apiClient.get("/marketing/campaigns").then((r) => r.data),
  getCampaign: (id) => apiClient.get(`/marketing/campaigns/${id}`).then((r) => r.data),
  createCampaign: (payload) => apiClient.post("/marketing/campaigns", payload).then((r) => r.data),
  updateCampaign: (id, patch) => apiClient.patch(`/marketing/campaigns/${id}`, patch).then((r) => r.data),
  sendCampaign: (id) => apiClient.post(`/marketing/campaigns/${id}/send`).then((r) => r.data),
  deleteCampaign: (id) => apiClient.delete(`/marketing/campaigns/${id}`).then((r) => r.data),
  listEmailTemplates: () => apiClient.get("/marketing/email-templates").then((r) => r.data),
  saveEmailTemplate: (payload) => apiClient.post("/marketing/email-templates", payload).then((r) => r.data),
  deleteEmailTemplate: (id) => apiClient.delete(`/marketing/email-templates/${id}`).then((r) => r.data),
  uploadAsset: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiClient.post("/marketing/assets", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
};

// ----- Forms (embeddable lead-capture) -----
export const formsApi = {
  list: () => apiClient.get("/marketing/forms").then((r) => r.data),
  get: (id) => apiClient.get(`/marketing/forms/${id}`).then((r) => r.data),
  create: (name) => apiClient.post("/marketing/forms", { name }).then((r) => r.data),
  update: (id, patch) => apiClient.put(`/marketing/forms/${id}`, patch).then((r) => r.data),
  remove: (id) => apiClient.delete(`/marketing/forms/${id}`).then((r) => r.data),
  submissions: (id) => apiClient.get(`/marketing/forms/${id}/submissions`).then((r) => r.data),
  convertLead: (subId) => apiClient.post(`/marketing/forms/submissions/${subId}/convert`).then((r) => r.data),
  archiveLead: (subId) => apiClient.post(`/marketing/forms/submissions/${subId}/archive`).then((r) => r.data),
  uploadLogo: (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiClient.post(`/marketing/forms/${id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  removeLogo: (id) => apiClient.delete(`/marketing/forms/${id}/logo`).then((r) => r.data),
};

// ----- Public forms (no auth — embedded/hosted) -----
export const publicFormsApi = {
  get: (id) => apiClient.get(`/public/forms/${id}`).then((r) => r.data),
  submit: (id, values) => apiClient.post(`/public/forms/${id}/submit`, { values }).then((r) => r.data),
};

// ----- Job titles -----
export const jobTitlesApi = {
  list: () => apiClient.get("/job-titles").then((r) => r.data),
  create: (data) => apiClient.post("/job-titles", data).then((r) => r.data),
  update: (id, patch) => apiClient.patch(`/job-titles/${id}`, patch).then((r) => r.data),
  remove: (id) => apiClient.delete(`/job-titles/${id}`).then((r) => r.data),
};

// ----- Payroll (admin-only on the backend) -----
// ----- Time clock (Clock In / Lunch / Clock Out) -----
export const timeclockApi = {
  me: () => apiClient.get(`/timeclock/me`).then((r) => r.data),
  clockIn: (gps) => apiClient.post(`/timeclock/clock-in`, gps || {}).then((r) => r.data),
  clockOut: (gps) => apiClient.post(`/timeclock/clock-out`, gps || {}).then((r) => r.data),
  lunchStart: () => apiClient.post(`/timeclock/lunch/start`).then((r) => r.data),
  lunchEnd: () => apiClient.post(`/timeclock/lunch/end`).then((r) => r.data),
  hours: (fromDate, toDate) =>
    apiClient.get(`/timeclock/hours`, { params: { fromDate, toDate } }).then((r) => r.data),
  entries: (fromDate, toDate, userId) =>
    apiClient.get(`/timeclock/entries`, { params: { fromDate, toDate, userId } }).then((r) => r.data),
};

// ----- Location logs (GPS-tagged clock/status audit trail) -----
export const locationLogsApi = {
  list: (params = {}) => apiClient.get(`/location-logs`, { params }).then((r) => r.data),
  summary: (params = {}) => apiClient.get(`/location-logs/summary`, { params }).then((r) => r.data),
};

// ----- Reputation Management (star-rating review requests) -----
export const reputationApi = {
  getSettings: () => apiClient.get(`/reputation/settings`).then((r) => r.data),
  updateSettings: (patch) => apiClient.put(`/reputation/settings`, patch).then((r) => r.data),
  listResponses: () => apiClient.get(`/reputation/responses`).then((r) => r.data),
  sendReviewRequest: (jobId, force = false) =>
    apiClient.post(`/reputation/jobs/${jobId}/send-review-request`, null, { params: { force } }).then((r) => r.data),
  sendManualRequest: (email) => apiClient.post(`/reputation/send-manual`, { email }).then((r) => r.data),
  publicGet: (token) => apiClient.get(`/public/reputation/${token}`).then((r) => r.data),
  publicSubmit: (token, payload) => apiClient.post(`/public/reputation/${token}/submit`, payload).then((r) => r.data),
};

// ----- Mobile-tech read-only pipeline view -----
export const pipelineTechApi = {
  pipelines: () => apiClient.get(`/pipeline-tech/pipelines`).then((r) => r.data),
  board: (pipelineId) => apiClient.get(`/pipeline-tech/boards/${pipelineId}`).then((r) => r.data),
};



export const payrollApi = {
  listOverrides: (fromDate, toDate) =>
    apiClient.get(`/payroll/overrides`, { params: { fromDate, toDate } }).then((r) => r.data),
  upsertOverride: (techId, fromDate, toDate, patch) =>
    apiClient
      .put(`/payroll/overrides/${techId}`, patch, { params: { fromDate, toDate } })
      .then((r) => r.data),
  removeOverride: (techId, fromDate, toDate) =>
    apiClient
      .delete(`/payroll/overrides/${techId}`, { params: { fromDate, toDate } })
      .then((r) => r.data),
  emailPaystub: (techId, fromDate, toDate, payload) =>
    apiClient
      .post(`/payroll/email/${techId}`, payload, { params: { fromDate, toDate } })
      .then((r) => r.data),
  downloadPaystub: async (techId, fromDate, toDate, payload) => {
    const res = await apiClient.post(`/payroll/paystub/${techId}/pdf`, payload, {
      params: { fromDate, toDate },
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `paystub_${techId}_${fromDate}_${toDate}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
  getRun: (fromDate, toDate) =>
    apiClient.get(`/payroll/run`, { params: { fromDate, toDate } }).then((r) => r.data),
  lockRun: (fromDate, toDate, frozenRows) =>
    apiClient
      .post(`/payroll/run/lock`, { frozenRows }, { params: { fromDate, toDate } })
      .then((r) => r.data),
  unlockRun: (fromDate, toDate) =>
    apiClient
      .post(`/payroll/run/unlock`, {}, { params: { fromDate, toDate } })
      .then((r) => r.data),
  // Accountant workflow (periods/adjustments/audit) — Phase 0/1
  getPeriodByRange: (startDate, endDate) =>
    apiClient.get(`/payroll/periods/by-range`, { params: { startDate, endDate } }).then((r) => r.data),
  createPeriod: (payload) => apiClient.post(`/payroll/periods`, payload).then((r) => r.data),
  patchPeriod: (id, payload) => apiClient.patch(`/payroll/periods/${id}`, payload).then((r) => r.data),
  transitionPeriod: (id, payload) => apiClient.post(`/payroll/periods/${id}/transition`, payload).then((r) => r.data),
  listPeriodAudit: (id) => apiClient.get(`/payroll/periods/${id}/audit`).then((r) => r.data),
  listAdjustments: (periodId, employeeId) =>
    apiClient
      .get(`/payroll/periods/${periodId}/adjustments`, { params: employeeId ? { employeeId } : {} })
      .then((r) => r.data),
  listJobAdjustments: (periodId, jobId) =>
    apiClient.get(`/payroll/periods/${periodId}/adjustments`, { params: { jobId } }).then((r) => r.data),
  createAdjustment: (periodId, payload) =>
    apiClient.post(`/payroll/periods/${periodId}/adjustments`, payload).then((r) => r.data),
  updateAdjustment: (adjustmentId, payload) =>
    apiClient.patch(`/payroll/adjustments/${adjustmentId}`, payload).then((r) => r.data),
  deleteAdjustment: (adjustmentId) =>
    apiClient.delete(`/payroll/adjustments/${adjustmentId}`).then((r) => r.data),
  taxPreview: (fromDate, toDate, rows) =>
    apiClient.post(`/payroll/tax-preview`, { fromDate, toDate, rows }).then((r) => r.data),
  getBalances: (employeeId) =>
    apiClient.get(`/payroll/balances/${employeeId}`).then((r) => r.data),
};

// ----- Documents — PDFs + Email (Phase C) -----
// PDFs are streamed as a blob (auth header attached by the axios client).
// Mobile browsers/PWAs largely ignore the `download` attribute on blob URLs and
// can block a `target=_blank` anchor click once the network round-trip has run,
// so on mobile we prefer the native share sheet, then fall back to opening the
// PDF in a new (or same) tab. Desktop keeps the original anchor-download flow.
function _isMobileDevice() {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || "");
}

async function _downloadDocPdf(path, filename) {
  const resp = await apiClient.get(path, { responseType: "blob" });
  const blob = new Blob([resp.data], { type: "application/pdf" });

  if (!_isMobileDevice()) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      a.remove();
      window.URL.revokeObjectURL(url);
    }, 1000);
    return;
  }

  // Mobile / PWA — try the native share sheet first so the user can save or print.
  if (navigator.canShare && typeof File !== "undefined") {
    try {
      const file = new File([blob], filename, { type: "application/pdf" });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: filename });
        return;
      }
    } catch (err) {
      if (err?.name === "AbortError") return; // user dismissed the share sheet — not a failure
      // any other share error falls through to the tab-open fallback below
    }
  }

  const url = window.URL.createObjectURL(blob);
  const win = window.open(url, "_blank");
  if (!win) window.location.href = url;
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
}

export const documentsApi = {
  downloadEstimatePdf: (id) => _downloadDocPdf(`/estimates/${id}/pdf`, `${id}.pdf`),
  downloadInvoicePdf: (id) => _downloadDocPdf(`/invoices/${id}/pdf`, `${id}.pdf`),
  emailEstimate: (id, payload) =>
    apiClient.post(`/estimates/${id}/email`, payload).then((r) => r.data),
  emailInvoice: (id, payload) =>
    apiClient.post(`/invoices/${id}/email`, payload).then((r) => r.data),
};

// ----- Online payments (Phase D) — public endpoints, no auth required -----
export const paymentsApi = {
  getInvoice: (id) => apiClient.get(`/pay/invoice/${id}`).then((r) => r.data),
  createCheckout: (id, originUrl, amount, opts = {}) =>
    apiClient
      .post(`/pay/checkout/${id}`, {
        origin_url: originUrl, amount: amount ?? null, embedded: !!opts.embedded,
      })
      .then((r) => r.data),
  status: (sessionId) => apiClient.get(`/pay/status/${sessionId}`).then((r) => r.data),
  // Phase E — estimate deposits (public)
  getEstimate: (id) => apiClient.get(`/pay/estimate/${id}`).then((r) => r.data),
  acceptEstimate: (id, payload) =>
    apiClient.post(`/pay/estimate/${id}/accept`, payload).then((r) => r.data),
  createDepositCheckout: (id, originUrl, amount, opts = {}) =>
    apiClient
      .post(`/pay/deposit/${id}`, {
        origin_url: originUrl, amount, embedded: !!opts.embedded,
      })
      .then((r) => r.data),
};

// ----- Platform super-admin (multi-tenant SaaS) -----
export const platformApi = {
  metrics: () => apiClient.get("/platform/metrics").then((r) => r.data),
  listCompanies: () => apiClient.get("/platform/companies").then((r) => r.data),
  getCompany: (id) => apiClient.get(`/platform/companies/${id}`).then((r) => r.data),
  createCompany: (data) => apiClient.post("/platform/companies", data).then((r) => r.data),
  updateCompany: (id, patch) => apiClient.patch(`/platform/companies/${id}`, patch).then((r) => r.data),
  applyPromo: (id, body) => apiClient.post(`/platform/companies/${id}/promo`, body).then((r) => r.data),
  clearPromo: (id) => apiClient.post(`/platform/companies/${id}/promo`, { clear: true }).then((r) => r.data),
  deleteCompany: (id) => apiClient.delete(`/platform/companies/${id}`).then((r) => r.data),
  listPlans: () => apiClient.get("/platform/plans").then((r) => r.data),
  createPlan: (data) => apiClient.post("/platform/plans", data).then((r) => r.data),
  updatePlan: (id, patch) => apiClient.patch(`/platform/plans/${id}`, patch).then((r) => r.data),
  deletePlan: (id) => apiClient.delete(`/platform/plans/${id}`).then((r) => r.data),
  // account management
  createCompanyUser: (id, data) => apiClient.post(`/platform/companies/${id}/users`, data).then((r) => r.data),
  inviteCompanyUser: (id, data) => apiClient.post(`/platform/companies/${id}/invite`, data).then((r) => r.data),
  updateUser: (uid, patch) => apiClient.patch(`/platform/users/${uid}`, patch).then((r) => r.data),
  resetPasswordManual: (uid, password) => apiClient.post(`/platform/users/${uid}/reset-password`, { password }).then((r) => r.data),
  resetPasswordEmail: (uid) => apiClient.post(`/platform/users/${uid}/reset-password-email`).then((r) => r.data),
  deleteUser: (uid) => apiClient.delete(`/platform/users/${uid}`).then((r) => r.data),
  grantDays: (id, days) => apiClient.post(`/platform/companies/${id}/grant-days`, { days }).then((r) => r.data),
  // Demo login tiles (public Login page uses GET /auth/demo-accounts).
  getDemoAccounts: () => apiClient.get(`/platform/demo-accounts`).then((r) => r.data),
  saveDemoAccounts: (payload) => apiClient.put(`/platform/demo-accounts`, payload).then((r) => r.data),
  voiceUsage: () => apiClient.get(`/platform/voice-usage`).then((r) => r.data),
  getAiRates: () => apiClient.get(`/platform/ai-rates`).then((r) => r.data),
  updateAiRates: (data) => apiClient.put(`/platform/ai-rates`, data).then((r) => r.data),
  // AI credit economics (config + packages for sale)
  getCreditConfig: () => apiClient.get(`/platform/credits/config`).then((r) => r.data),
  updateCreditConfig: (data) => apiClient.put(`/platform/credits/config`, data).then((r) => r.data),
  listCreditPackages: () => apiClient.get(`/platform/credits/packages`).then((r) => r.data),
  creditMargin: () => apiClient.get(`/platform/credits/margin`).then((r) => r.data),
  createCreditPackage: (data) => apiClient.post(`/platform/credits/packages`, data).then((r) => r.data),
  updateCreditPackage: (id, patch) => apiClient.put(`/platform/credits/packages/${id}`, patch).then((r) => r.data),
  deleteCreditPackage: (id) => apiClient.delete(`/platform/credits/packages/${id}`).then((r) => r.data),
  impersonate: (id) => apiClient.post(`/platform/companies/${id}/impersonate`).then((r) => r.data),
  emailAccount: (id, data) => apiClient.post(`/platform/companies/${id}/email`, data).then((r) => r.data),
  addNote: (id, text) => apiClient.post(`/platform/companies/${id}/notes`, { text }).then((r) => r.data),
  deleteNote: (id, noteId) => apiClient.delete(`/platform/companies/${id}/notes/${noteId}`).then((r) => r.data),
  createInvoice: (id, data) => apiClient.post(`/platform/companies/${id}/invoices`, data).then((r) => r.data),
  seatEvents: (id) => apiClient.get(`/platform/companies/${id}/seat-events`).then((r) => r.data),
  runRenewals: () => apiClient.post(`/platform/billing/run-renewals`).then((r) => r.data),
  mrrExportUrl: () => `${BASE_URL}/platform/billing/mrr-export`,
  emailInvoice: (invId) => apiClient.post(`/platform/invoices/${invId}/email`).then((r) => r.data),
  payLink: (invId, originUrl) => apiClient.post(`/platform/invoices/${invId}/pay-link`, { origin_url: originUrl }).then((r) => r.data),
  markInvoicePaid: (invId, data) => apiClient.post(`/platform/invoices/${invId}/mark-paid`, data).then((r) => r.data),
  voidInvoice: (invId) => apiClient.post(`/platform/invoices/${invId}/void`).then((r) => r.data),
  stripe: {
    getSettings: () => apiClient.get("/platform/stripe/settings").then((r) => r.data),
    updateSettings: (data) => apiClient.put("/platform/stripe/settings", data).then((r) => r.data),
    testConnection: () => apiClient.post("/platform/stripe/test").then((r) => r.data),
  },
  modules: () => apiClient.get("/platform/modules").then((r) => r.data),
  // UI Theme Studio
  email: {
    getSettings: () => apiClient.get("/platform/email/settings").then((r) => r.data),
    updateSettings: (patch) => apiClient.put("/platform/email/settings", patch).then((r) => r.data),
    sendTest: (to) => apiClient.post("/platform/email/test", { to }).then((r) => r.data),
  },
  themes: {
    list: () => apiClient.get("/platform/themes").then((r) => r.data),
    create: (data) => apiClient.post("/platform/themes", data).then((r) => r.data),
    update: (id, patch) => apiClient.put(`/platform/themes/${id}`, patch).then((r) => r.data),
    remove: (id) => apiClient.delete(`/platform/themes/${id}`).then((r) => r.data),
    setGlobal: (themeId) => apiClient.put("/platform/themes/global", { themeId }).then((r) => r.data),
    assignCompany: (companyId, themeId) =>
      apiClient.put(`/platform/companies/${companyId}/theme`, { themeId }).then((r) => r.data),
    uploadBackground: (file) => {
      const fd = new FormData();
      fd.append("file", file);
      return apiClient
        .post("/platform/themes/background", fd, { headers: { "Content-Type": "multipart/form-data" } })
        .then((r) => r.data);
    },
  },
  // global product branding (sidebar logo + "Powered by" label)
  getBranding: () => apiClient.get("/platform/branding").then((r) => r.data),
  updateBranding: (patch) => apiClient.put("/platform/branding", patch).then((r) => r.data),
  uploadBrandingLogo: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiClient
      .post("/platform/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
  removeBrandingLogo: () => apiClient.delete("/platform/branding/logo").then((r) => r.data),
  // Demo requests (super-admin management) — Phase 1 of the Book-a-demo flow.
  listDemoRequests: () => apiClient.get("/demo-requests").then((r) => r.data),
  updateDemoRequest: (id, patch) => apiClient.patch(`/demo-requests/${id}`, patch).then((r) => r.data),
  deleteDemoRequest: (id) => apiClient.delete(`/demo-requests/${id}`).then((r) => r.data),

  popups: {
    list: () => apiClient.get("/platform/popups").then((r) => r.data),
    get: (id) => apiClient.get(`/platform/popups/${id}`).then((r) => r.data),
    create: (body) => apiClient.post("/platform/popups", body).then((r) => r.data),
    update: (id, body) => apiClient.put(`/platform/popups/${id}`, body).then((r) => r.data),
    remove: (id) => apiClient.delete(`/platform/popups/${id}`).then((r) => r.data),
    activate: (id) => apiClient.post(`/platform/popups/${id}/activate`).then((r) => r.data),
    deactivate: (id) => apiClient.post(`/platform/popups/${id}/deactivate`).then((r) => r.data),
  },
  helpCenter: {
    listArticles: () => apiClient.get("/platform/help-center/articles").then((r) => r.data),
    createArticle: (data) => apiClient.post("/platform/help-center/articles", data).then((r) => r.data),
    updateArticle: (id, patch) => apiClient.patch(`/platform/help-center/articles/${id}`, patch).then((r) => r.data),
    removeArticle: (id) => apiClient.delete(`/platform/help-center/articles/${id}`).then((r) => r.data),
    bulkUpload: (file) => {
      const fd = new FormData();
      fd.append("file", file);
      return apiClient
        .post("/platform/help-center/articles/bulk-upload", fd, { headers: { "Content-Type": "multipart/form-data" } })
        .then((r) => r.data);
    },
    getChatbotSettings: () => apiClient.get("/platform/help-center/chatbot-settings").then((r) => r.data),
    updateChatbotSettings: (data) => apiClient.put("/platform/help-center/chatbot-settings", data).then((r) => r.data),
  },
};

// ----- Platform Console: Affiliate Management (super-admin) -----
export const affiliatesAdminApi = {
  list: () => apiClient.get("/platform/affiliates").then((r) => r.data),
  create: (data) => apiClient.post("/platform/affiliates", data).then((r) => r.data),
  update: (id, patch) => apiClient.patch(`/platform/affiliates/${id}`, patch).then((r) => r.data),
  remove: (id) => apiClient.delete(`/platform/affiliates/${id}`).then((r) => r.data),
  getSettings: () => apiClient.get("/platform/affiliates/settings").then((r) => r.data),
  updateSettings: (patch) => apiClient.patch("/platform/affiliates/settings", patch).then((r) => r.data),
  listCommissions: (params = {}) => apiClient.get("/platform/affiliates/commissions", { params }).then((r) => r.data),
  markCommissionsPaid: (ids) => apiClient.post("/platform/affiliates/commissions/mark-paid", { ids }).then((r) => r.data),
  listApplications: () => apiClient.get("/affiliate-applications").then((r) => r.data),
  updateApplication: (id, patch) => apiClient.patch(`/affiliate-applications/${id}`, patch).then((r) => r.data),
  removeApplication: (id) => apiClient.delete(`/affiliate-applications/${id}`).then((r) => r.data),
  detail: (id) => apiClient.get(`/platform/affiliates/${id}/detail`).then((r) => r.data),
  payrollPending: () => apiClient.get("/platform/affiliates/payroll/pending").then((r) => r.data),
  processPayroll: (payoutFriday) => apiClient.post("/platform/affiliates/payroll/process", { payoutFriday }).then((r) => r.data),
};

// Public 'Become an Affiliate' submission (no auth) — used by the marketing landing page footer.
export const affiliateApplicationsApi = {
  create: (data) => apiClient.post("/affiliate-applications", data).then((r) => r.data),
};

// ----- Affiliate Portal (partner self-service, separate login session) -----
export const affiliateApi = {
  login: (email, password) => apiClient.post("/affiliate/login", { email, password }).then((r) => r.data),
  logout: () => apiClient.post("/affiliate/logout").then((r) => r.data),
  me: () => apiClient.get("/affiliate/me").then((r) => r.data),
  dashboard: () => apiClient.get("/affiliate/dashboard").then((r) => r.data),
  updateProfile: (patch) => apiClient.patch("/affiliate/me", patch).then((r) => r.data),
  changePassword: (currentPassword, newPassword) =>
    apiClient.post("/affiliate/change-password", { currentPassword, newPassword }).then((r) => r.data),
  updatePayout: (patch) => apiClient.patch("/affiliate/payout", patch).then((r) => r.data),
  connectStripe: (code) => apiClient.post("/affiliate/stripe/connect", { code }).then((r) => r.data),
  validateCoupon: (code) => apiClient.get("/affiliate/validate-coupon", { params: { code } }).then((r) => r.data),
  payrollCurrent: () => apiClient.get("/affiliate/payroll/current").then((r) => r.data),
  payrollHistory: () => apiClient.get("/affiliate/payroll/history").then((r) => r.data),
};

// Public 'Book a demo' submission (no auth) — used by the marketing landing page.
export const demoRequestsApi = {
  create: (data) => apiClient.post("/demo-requests", data).then((r) => r.data),
};

export const demoBookingApi = {
  // Public (website popup)
  publicConfig: () => apiClient.get("/demo-booking/public").then((r) => r.data),
  publicSlots: (start, days) => apiClient.get("/demo-booking/slots", { params: { start, days } }).then((r) => r.data),
  book: (payload) => apiClient.post("/demo-booking", payload).then((r) => r.data),
  // Admin (Platform Console)
  getSettings: () => apiClient.get("/demo-booking/settings").then((r) => r.data),
  updateSettings: (payload) => apiClient.put("/demo-booking/settings", payload).then((r) => r.data),
};

export const platformCalendarApi = {
  availableSlots: (date) => apiClient.get("/calendar/available-slots", { params: { date } }).then((r) => r.data),
  appointments: (start, end) => apiClient.get("/calendar/platform/appointments", { params: { start, end } }).then((r) => r.data),
  entries: (start, end) => apiClient.get("/calendar/platform/entries", { params: { start, end } }).then((r) => r.data),
  createEntry: (payload) => apiClient.post("/calendar/platform/entries", payload).then((r) => r.data),
  deleteEntry: (id) => apiClient.delete(`/calendar/platform/entries/${id}`).then((r) => r.data),
};

// Public global platform branding (no auth) — consumed by the sidebar + PoweredBy.
export const platformBrandingApi = {
  getPublic: () => apiClient.get("/platform-branding").then((r) => r.data),
};

// Resolved UI theme tokens for the current tenant (auth required).
export const themeApi = {
  active: () => apiClient.get("/theme/active").then((r) => r.data),
};

// ----- Billing & self-serve signup -----
export const billingApi = {
  signupCompany: (data) => apiClient.post("/signup/company", data).then((r) => r.data),
  validatePromo: (code) => apiClient.get(`/promo/validate?code=${encodeURIComponent(code)}`).then((r) => r.data),
  state: () => apiClient.get("/billing/state").then((r) => r.data),
  plans: () => apiClient.get("/billing/plans").then((r) => r.data),
  scheduleChange: (body) => apiClient.post("/billing/schedule-change", body).then((r) => r.data),
  cancelScheduledChange: () => apiClient.post("/billing/cancel-scheduled-change").then((r) => r.data),
  addSeats: (seatsToAdd) => apiClient.post("/billing/seats", { seatsToAdd }).then((r) => r.data),
  removeSeats: (seatsToRemove) => apiClient.post("/billing/seats/remove", { seatsToRemove }).then((r) => r.data),
  resetSeats: () => apiClient.post("/billing/seats/reset").then((r) => r.data),
  activateSeats: (targetSeats, originUrl) =>
    apiClient.post("/billing/seats/activate", { targetSeats, origin_url: originUrl }).then((r) => r.data),
  // Ship 1: per-user activation (kills the seat-count model). `embedded=true`
  // makes the backend return a Stripe client_secret for in-page checkout.
  activationStatus: () => apiClient.get("/billing/activation-status").then((r) => r.data),
  activateUsers: (userIds, term, originUrl, opts = {}) =>
    apiClient.post("/billing/users/activate", {
      userIds, term, origin_url: originUrl, embedded: !!opts.embedded,
    }).then((r) => r.data),
  // Ship 2: prepay a NEW-user activation before create/invite (post-trial only).
  // Multi-add supported via `prefills[]` (each row has its own term).
  prepayNewUser: (body) =>
    apiClient.post("/billing/users/prepay", body).then((r) => r.data),
  prepayStatus: (sessionId) =>
    apiClient.get(`/billing/users/prepay-status/${sessionId}`).then((r) => r.data),
  checkout: (originUrl, opts = {}) =>
    apiClient.post("/billing/checkout", {
      origin_url: originUrl,
      ...opts,
    }).then((r) => r.data),
  status: (sessionId) => apiClient.get(`/billing/status/${sessionId}`).then((r) => r.data),
  // Stripe Customer Portal — admin updates card, downloads invoices, cancels.
  // Requires the company to have subscribed at least once.
  portal: (originUrl) =>
    apiClient.post("/billing/portal", { origin_url: originUrl }).then((r) => r.data),
};

// ----- AI Credits (prepaid wallet) -----
export const creditsApi = {
  balance: () => apiClient.get("/credits/balance").then((r) => r.data),
  packages: () => apiClient.get("/credits/packages").then((r) => r.data),
  ledger: (limit = 50) => apiClient.get(`/credits/ledger?limit=${limit}`).then((r) => r.data),
  checkout: (packageId, originUrl, opts = {}) =>
    apiClient.post("/billing/credits/checkout", {
      packageId, origin_url: originUrl, embedded: !!opts.embedded,
    }).then((r) => r.data),
  status: (sessionId) => apiClient.get(`/billing/status/${sessionId}`).then((r) => r.data),
};

// Public plans list for the signup page (no auth) — reuse the platform plans
// endpoint is auth-gated, so expose plans through billing state isn't ideal;
// the signup page fetches plans via a dedicated public-safe call below.
export const publicPlansApi = {
  // Plans are needed pre-auth on /signup. We surface them via the platform
  // metrics-free path: a lightweight public list is added on the backend.
  list: () => apiClient.get("/plans/public").then((r) => r.data),
};

// ----- Public SEO resources (blog/guides/tools) -----
export const resourcesApi = {
  list: (category) => apiClient.get("/resources/articles", { params: category ? { category } : {} }).then((r) => r.data),
  get: (slug) => apiClient.get(`/resources/articles/${slug}`).then((r) => r.data),
};

// ----- Platform admin: SEO resources CRUD -----
export const adminResourcesApi = {
  list: () => apiClient.get("/platform/resources/articles").then((r) => r.data),
  create: (payload) => apiClient.post("/platform/resources/articles", payload).then((r) => r.data),
  update: (id, patch) => apiClient.patch(`/platform/resources/articles/${id}`, patch).then((r) => r.data),
  remove: (id) => apiClient.delete(`/platform/resources/articles/${id}`).then((r) => r.data),
  seoAudit: () => apiClient.get("/platform/resources/seo-audit").then((r) => r.data),
};

// ----- Onboarding checklist (company admin) -----
export const onboardingApi = {
  checklist: () => apiClient.get("/onboarding/checklist").then((r) => r.data),
  markManual: (key) => apiClient.post("/onboarding/mark-manual", { key }).then((r) => r.data),
};

// ----- Done for You Onboarding purchase (company admin) -----
export const onboardingPlansApi = {
  list: () => apiClient.get("/onboarding-plans").then((r) => r.data),
  checkout: (planId, originUrl) =>
    apiClient.post(`/onboarding-plans/${planId}/checkout`, { origin_url: originUrl }).then((r) => r.data),
  status: (sessionId) => apiClient.get(`/onboarding-plans/checkout/${sessionId}/status`).then((r) => r.data),
  submitIntake: (sessionId, values) =>
    apiClient.post(`/onboarding-plans/checkout/${sessionId}/submit-intake`, { values }).then((r) => r.data),
};

// ----- Staff onboarding (role-specific setup after a teammate accepts) -----
export const staffOnboardingApi = {
  state: () => apiClient.get("/staff-onboarding/state").then((r) => r.data),
  save: (data) => apiClient.post("/staff-onboarding/save", data).then((r) => r.data),
  complete: () => apiClient.post("/staff-onboarding/complete").then((r) => r.data),
};

// ----- Company onboarding (owner setup wizard right after self-serve signup) -----
export const companyOnboardingApi = {
  state: () => apiClient.get("/company-onboarding/state").then((r) => r.data),
  save: (data) => apiClient.post("/company-onboarding/save", data).then((r) => r.data),
  complete: () => apiClient.post("/company-onboarding/complete").then((r) => r.data),
};

// ----- Pipeline (Kanban) -----
export const pipelineApi = {
  stages: (board) => apiClient.get(`/pipeline/stages`, { params: { board } }).then((r) => r.data),
  createStage: (data) => apiClient.post(`/pipeline/stages`, data).then((r) => r.data),
  updateStage: (id, patch) => apiClient.patch(`/pipeline/stages/${id}`, patch).then((r) => r.data),
  deleteStage: (id) => apiClient.delete(`/pipeline/stages/${id}`).then((r) => r.data),
  reorderStages: (board, orderedIds) =>
    apiClient.put(`/pipeline/stages/reorder`, { board, orderedIds }).then((r) => r.data),
  // Custom pipelines (boards)
  pipelines: () => apiClient.get(`/pipeline/pipelines`).then((r) => r.data),
  createPipeline: (data) => apiClient.post(`/pipeline/pipelines`, data).then((r) => r.data),
  updatePipeline: (id, patch) => apiClient.patch(`/pipeline/pipelines/${id}`, patch).then((r) => r.data),
  deletePipeline: (id) => apiClient.delete(`/pipeline/pipelines/${id}`).then((r) => r.data),
  // Custom pipeline cards
  cards: (pipelineId) => apiClient.get(`/pipeline/cards`, { params: { pipelineId } }).then((r) => r.data),
  myLeads: () => apiClient.get(`/pipeline/my-leads`).then((r) => r.data),
  createCard: (data) => apiClient.post(`/pipeline/cards`, data).then((r) => r.data),
  updateCard: (id, patch) => apiClient.patch(`/pipeline/cards/${id}`, patch).then((r) => r.data),
  deleteCard: (id) => apiClient.delete(`/pipeline/cards/${id}`).then((r) => r.data),
};

// ----- Permission Types (editable RBAC, frontend-enforced) -----
export const permissionsApi = {
  modules: () => apiClient.get("/permissions/modules").then((r) => r.data),
  me: () => apiClient.get("/permissions/me").then((r) => r.data),
  types: () => apiClient.get("/permissions/types").then((r) => r.data),
  createType: (data) => apiClient.post("/permissions/types", data).then((r) => r.data),
  updateType: (id, patch) => apiClient.patch(`/permissions/types/${id}`, patch).then((r) => r.data),
  deleteType: (id) => apiClient.delete(`/permissions/types/${id}`).then((r) => r.data),
};

// ----- Pipeline automations (Phase B) -----
export const automationsApi = {
  meta: () => apiClient.get("/automations/meta").then((r) => r.data),
  recipes: () => apiClient.get("/automations/recipes").then((r) => r.data),
  list: () => apiClient.get("/automations").then((r) => r.data),
  get: (id) => apiClient.get(`/automations/${id}`).then((r) => r.data),
  create: (data) => apiClient.post("/automations", data).then((r) => r.data),
  update: (id, patch) => apiClient.patch(`/automations/${id}`, patch).then((r) => r.data),
  remove: (id) => apiClient.delete(`/automations/${id}`).then((r) => r.data),
  runs: (id) => apiClient.get(`/automations/${id}/runs`).then((r) => r.data),
  testRun: (id, cardId) => apiClient.post(`/automations/${id}/test-run`, { cardId }).then((r) => r.data),
};

// ----- AI Email Designer -----
export const aiEmailApi = {
  design: (brief) => apiClient.post("/ai-email/design", brief).then((r) => r.data),
  suggestAutomations: (payload) => apiClient.post("/ai-email/suggest-automations", payload).then((r) => r.data),
  generateImage: (prompt) => apiClient.post("/ai-email/image/generate", { prompt }).then((r) => r.data),
  editImage: (file, instruction) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("instruction", instruction);
    return apiClient.post("/ai-email/image/edit", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
};

// ----- Tasks (follow-up tasks; manual + automation-created) -----
export const entitlementsApi = {
  me: () => apiClient.get("/me/entitlements").then((r) => r.data),
};

// ----- AI Voice Agents (per-company; admin-only writes; gated by `ai_agents` module) -----
export const aiAgentsApi = {
  meta: () => apiClient.get("/ai-agents/meta").then((r) => r.data),
  listAgents: (params) => apiClient.get("/ai-agents/agents", { params }).then((r) => r.data),
  createAgent: (data) => apiClient.post("/ai-agents/agents", data).then((r) => r.data),
  updateAgent: (id, patch) => apiClient.patch(`/ai-agents/agents/${id}`, patch).then((r) => r.data),
  removeAgent: (id) => apiClient.delete(`/ai-agents/agents/${id}`).then((r) => r.data),
  listCampaigns: () => apiClient.get("/ai-agents/campaigns").then((r) => r.data),
  createCampaign: (data) => apiClient.post("/ai-agents/campaigns", data).then((r) => r.data),
  updateCampaign: (id, patch) => apiClient.patch(`/ai-agents/campaigns/${id}`, patch).then((r) => r.data),
  removeCampaign: (id) => apiClient.delete(`/ai-agents/campaigns/${id}`).then((r) => r.data),
  // Knowledge Base
  listKnowledge: () => apiClient.get("/ai-agents/knowledge").then((r) => r.data),
  getKnowledge: (id) => apiClient.get(`/ai-agents/knowledge/${id}`).then((r) => r.data),
  createKnowledge: (data) => apiClient.post("/ai-agents/knowledge", data).then((r) => r.data),
  uploadKnowledge: (file, title, category) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title || "");
    fd.append("category", category || "other");
    return apiClient.post("/ai-agents/knowledge/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  updateKnowledge: (id, patch) => apiClient.patch(`/ai-agents/knowledge/${id}`, patch).then((r) => r.data),
  removeKnowledge: (id) => apiClient.delete(`/ai-agents/knowledge/${id}`).then((r) => r.data),
  searchKnowledge: (query, topK) => apiClient.post("/ai-agents/knowledge/search", { query, topK }).then((r) => r.data),
  // Orchestration / Call Queue
  runCampaign: (id) => apiClient.post(`/ai-agents/campaigns/${id}/run-now`).then((r) => r.data),
  previewCampaign: (id) => apiClient.post(`/ai-agents/campaigns/${id}/preview`).then((r) => r.data),
  listCallJobs: (params) => apiClient.get("/ai-agents/call-jobs", { params }).then((r) => r.data),
  callJobStats: (params) => apiClient.get("/ai-agents/call-jobs/stats", { params }).then((r) => r.data),
  callJobContext: (id) => apiClient.get(`/ai-agents/call-jobs/${id}/context`).then((r) => r.data),
  cancelCallJob: (id) => apiClient.post(`/ai-agents/call-jobs/${id}/cancel`).then((r) => r.data),
  snoozeCallJob: (id) => apiClient.post(`/ai-agents/call-jobs/${id}/snooze`).then((r) => r.data),
  removeCallJob: (id) => apiClient.post(`/ai-agents/call-jobs/${id}/remove`).then((r) => r.data),
  loadMembershipCampaign: (id) => apiClient.post(`/ai-agents/campaigns/${id}/load-memberships`).then((r) => r.data),
  // Estimate enrollment lifecycle (Active/Inactive queue tabs)
  listEnrollments: (bucket) => apiClient.get("/ai-agents/enrollments", { params: { bucket } }).then((r) => r.data),
  removeEnrollment: (id) => apiClient.post(`/ai-agents/enrollments/${id}/remove`).then((r) => r.data),
  reactivateEnrollments: (ids) => apiClient.post("/ai-agents/enrollments/reactivate", { ids }).then((r) => r.data),
  // Inbound receptionist
  receptionist: () => apiClient.get("/ai-agents/receptionist").then((r) => r.data),
  saveReceptionist: (patch) => apiClient.put("/ai-agents/receptionist", patch).then((r) => r.data),
  receptionistCalls: () => apiClient.get("/ai-agents/receptionist/calls").then((r) => r.data),
  importReceptionistNumber: () => apiClient.post("/ai-agents/receptionist/import-number").then((r) => r.data),
  deactivateReceptionist: () => apiClient.post("/ai-agents/receptionist/deactivate").then((r) => r.data),
  engineStatus: () => apiClient.get("/ai-agents/engine-status").then((r) => r.data),
  placeCall: (id) => apiClient.post(`/ai-agents/call-jobs/${id}/place`).then((r) => r.data),
  simulateCall: (id, outcome, durationSec) => apiClient.post(`/ai-agents/call-jobs/${id}/simulate`, { outcome, durationSec }).then((r) => r.data),
  sendDoc: (id, channel) => apiClient.post(`/ai-agents/call-jobs/${id}/send-doc`, { channel }).then((r) => r.data),
  reports: (params) => apiClient.get("/ai-agents/reports", { params }).then((r) => r.data),
  // Call summary settings + unified call log
  callSummarySettings: () => apiClient.get("/ai-agents/call-summary/settings").then((r) => r.data),
  saveCallSummarySettings: (patch) => apiClient.put("/ai-agents/call-summary/settings", patch).then((r) => r.data),
  callSummaries: (params) => apiClient.get("/ai-agents/call-summaries", { params }).then((r) => r.data),
};

export const tasksApi = {
  list: (params) => apiClient.get("/tasks", { params }).then((r) => r.data),
  create: (data) => apiClient.post("/tasks", data).then((r) => r.data),
  update: (id, patch) => apiClient.patch(`/tasks/${id}`, patch).then((r) => r.data),
  remove: (id) => apiClient.delete(`/tasks/${id}`).then((r) => r.data),
  addNote: (id, text) => apiClient.post(`/tasks/${id}/notes`, { text }).then((r) => r.data),
  badge: () => apiClient.get("/tasks/badge").then((r) => r.data),
};

// ----- Internal Calendar (staff-facing, Google-Calendar-style) -----
export const calendarApi = {
  listCalendars: () => apiClient.get(`/calendar/calendars`).then((r) => r.data),
  createCalendar: (data) => apiClient.post(`/calendar/calendars`, data).then((r) => r.data),
  updateCalendar: (id, patch) => apiClient.patch(`/calendar/calendars/${id}`, patch).then((r) => r.data),
  removeCalendar: (id) => apiClient.delete(`/calendar/calendars/${id}`).then((r) => r.data),
  listEvents: (params) => apiClient.get(`/calendar/events`, { params }).then((r) => r.data),
  createEvent: (data) => apiClient.post(`/calendar/events`, data).then((r) => r.data),
  updateEvent: (id, patch) => apiClient.patch(`/calendar/events/${id}`, patch).then((r) => r.data),
  removeEvent: (id) => apiClient.delete(`/calendar/events/${id}`).then((r) => r.data),
};

// ----- SMS automations (Twilio, per-company, admin-only) -----
export const smsApi = {
  getSettings: () => apiClient.get("/sms/settings").then((r) => r.data),
  updateSettings: (patch) => apiClient.put("/sms/settings", patch).then((r) => r.data),
  sendTest: (to) => apiClient.post("/sms/test", { to }).then((r) => r.data),
  runReminders: () => apiClient.post("/sms/run-reminders").then((r) => r.data),
  numberState: () => apiClient.get("/sms/number").then((r) => r.data),
  searchNumbers: (areaCode) => apiClient.get("/sms/numbers/available", { params: { areaCode } }).then((r) => r.data),
  claimNumber: (phoneNumber) => apiClient.post("/sms/numbers/claim", { phoneNumber }).then((r) => r.data),
};

// ----- A2P 10DLC ISV onboarding (Trust Hub + Brand Registration, Phase 1) -----
export const twilioA2pApi = {
  status: () => apiClient.get("/twilio/a2p/status").then((r) => r.data),
  register: (payload) => apiClient.post("/twilio/a2p/register", payload).then((r) => r.data),
  registerCampaigns: () => apiClient.post("/twilio/a2p/register-campaigns").then((r) => r.data),
  getAppointmentSettings: () => apiClient.get("/twilio/a2p/appointment-settings").then((r) => r.data),
  saveAppointmentSettings: (payload) => apiClient.put("/twilio/a2p/appointment-settings", payload).then((r) => r.data),
};

// ----- Customer SMS consent capture (transactional/service messaging only) -----
export const smsConsentApi = {
  get: (customerId) => apiClient.get(`/customers/${customerId}/sms-consent`).then((r) => r.data),
  record: (customerId, payload) => apiClient.post(`/customers/${customerId}/sms-consent`, payload).then((r) => r.data),
};

// ----- Public tenant-specific SMS legal pages (no auth) -----
export const tenantLegalApi = {
  info: (slug) => apiClient.get(`/legal/${slug}/info`).then((r) => r.data),
};

// ----- Platform Console: ElevenLabs voice engine (super-admin) -----
export const platformVoiceApi = {
  config: () => apiClient.get("/platform/voice-engine/config").then((r) => r.data),
  saveConfig: (patch) => apiClient.put("/platform/voice-engine/config", patch).then((r) => r.data),
  test: () => apiClient.post("/platform/voice-engine/test").then((r) => r.data),
};

// ----- Platform Console: Twilio master account + subaccounts (super-admin) -----
export const platformTwilioApi = {
  overview: () => apiClient.get("/platform/twilio/overview").then((r) => r.data),
  saveConfig: (patch) => apiClient.put("/platform/twilio/config", patch).then((r) => r.data),
  test: () => apiClient.post("/platform/twilio/test").then((r) => r.data),
  provision: (companyId) => apiClient.post(`/platform/twilio/companies/${companyId}/provision`).then((r) => r.data),
  setStatus: (companyId, status) => apiClient.post(`/platform/twilio/companies/${companyId}/status`, { status }).then((r) => r.data),
  provisionAll: () => apiClient.post("/platform/twilio/provision-all").then((r) => r.data),
  syncBilling: () => apiClient.post("/platform/twilio/sync-billing").then((r) => r.data),
  liveUsage: (companyId) => apiClient.get(`/platform/twilio/companies/${companyId}/live-usage`).then((r) => r.data),
};


const FILE_BASE = process.env.REACT_APP_BACKEND_URL;

// ----- Personal profile (avatar + presence) — all roles -----
export const profileApi = {
  uploadAvatar: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiClient.post(`/profile/avatar`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  removeAvatar: () => apiClient.delete(`/profile/avatar`).then((r) => r.data),
  heartbeat: () => apiClient.post(`/profile/heartbeat`).then((r) => r.data),
};

// ----- Web push notifications (chat messages + job assignment) -----
export const pushApi = {
  vapidKey: () => apiClient.get(`/push/vapid-public-key`).then((r) => r.data.key),
  status: () => apiClient.get(`/push/status`).then((r) => r.data),
  setPreference: (permission) => apiClient.post(`/push/preference`, { permission }).then((r) => r.data),
  subscribe: (subscription) => apiClient.post(`/push/subscribe`, subscription).then((r) => r.data),
  unsubscribe: (endpoint) => apiClient.post(`/push/unsubscribe`, { endpoint }).then((r) => r.data),
  setActiveContext: (type, id) => apiClient.post(`/push/active-context`, { type, id }).then((r) => r.data),
  clearActiveContext: () => apiClient.post(`/push/active-context`, { type: null, id: null }).then((r) => r.data),
};

// ----- Company messaging (channels / groups / DMs) -----
export const chatApi = {
  members: () => apiClient.get(`/chat/members`).then((r) => r.data),
  conversations: () => apiClient.get(`/chat/conversations`).then((r) => r.data),
  publicChannels: () => apiClient.get(`/chat/channels/public`).then((r) => r.data),
  getConversation: (id) => apiClient.get(`/chat/conversations/${id}`).then((r) => r.data),
  createChannel: (data) => apiClient.post(`/chat/channels`, data).then((r) => r.data),
  startDm: (userId) => apiClient.post(`/chat/dm`, { userId }).then((r) => r.data),
  join: (id) => apiClient.post(`/chat/conversations/${id}/join`).then((r) => r.data),
  addMembers: (id, memberIds) => apiClient.post(`/chat/conversations/${id}/members`, { memberIds }).then((r) => r.data),
  leave: (id) => apiClient.post(`/chat/conversations/${id}/leave`).then((r) => r.data),
  messages: (id, params = {}) => apiClient.get(`/chat/conversations/${id}/messages`, { params }).then((r) => r.data),
  send: (id, data) => apiClient.post(`/chat/conversations/${id}/messages`, data).then((r) => r.data),
  edit: (msgId, text) => apiClient.patch(`/chat/messages/${msgId}`, { text }).then((r) => r.data),
  remove: (msgId) => apiClient.delete(`/chat/messages/${msgId}`).then((r) => r.data),
  react: (msgId, emoji) => apiClient.post(`/chat/messages/${msgId}/react`, { emoji }).then((r) => r.data),
  markRead: (id) => apiClient.post(`/chat/conversations/${id}/read`).then((r) => r.data),
  unread: () => apiClient.get(`/chat/unread`).then((r) => r.data),
  search: (q) => apiClient.get(`/chat/search`, { params: { q } }).then((r) => r.data),
  uploadAttachment: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiClient.post(`/chat/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  fileUrl: (relUrl) => (relUrl ? `${FILE_BASE}${relUrl}` : null),
};

// ----- Unified customer Inbox (SMS + Email) -----
export const inboxApi = {
  settings: () => apiClient.get(`/inbox/settings`).then((r) => r.data),
  conversations: () => apiClient.get(`/inbox/conversations`).then((r) => r.data),
  getConversation: (id) => apiClient.get(`/inbox/conversations/${id}`).then((r) => r.data),
  messages: (id, params = {}) => apiClient.get(`/inbox/conversations/${id}/messages`, { params }).then((r) => r.data),
  send: (id, data) => apiClient.post(`/inbox/conversations/${id}/send`, data).then((r) => r.data),
  compose: (data) => apiClient.post(`/inbox/compose`, data).then((r) => r.data),
  markRead: (id) => apiClient.post(`/inbox/conversations/${id}/read`).then((r) => r.data),
  unread: () => apiClient.get(`/inbox/unread`).then((r) => r.data),
  simulateInbound: (data) => apiClient.post(`/inbox/simulate-inbound`, data).then((r) => r.data),
  star: (id, important) => apiClient.post(`/inbox/conversations/${id}/star`, { important }).then((r) => r.data),
  deleteConversation: (id) => apiClient.delete(`/inbox/conversations/${id}`).then((r) => r.data),
  deleteMessage: (convId, msgId) => apiClient.delete(`/inbox/conversations/${convId}/messages/${msgId}`).then((r) => r.data),
  trash: () => apiClient.get(`/inbox/trash`).then((r) => r.data),
  restoreConversation: (id) => apiClient.post(`/inbox/conversations/${id}/restore`).then((r) => r.data),
  restoreMessage: (convId, msgId) => apiClient.post(`/inbox/conversations/${convId}/messages/${msgId}/restore`).then((r) => r.data),
  upload: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiClient.post(`/inbox/attachments`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
};

// ----- Pipeline lead workspace (drawer sub-resources) -----
export const pipelineLeadApi = {
  // Activities (auto-populated feed + manual notes)
  activities: (cardId) => apiClient.get(`/pipeline/cards/${cardId}/activities`).then((r) => r.data),
  addActivity: (cardId, data) => apiClient.post(`/pipeline/cards/${cardId}/activities`, data).then((r) => r.data),
  // Appointments
  appointments: (cardId) => apiClient.get(`/pipeline/cards/${cardId}/appointments`).then((r) => r.data),
  createAppointment: (cardId, data) => apiClient.post(`/pipeline/cards/${cardId}/appointments`, data).then((r) => r.data),
  updateAppointment: (cardId, aid, data) => apiClient.patch(`/pipeline/cards/${cardId}/appointments/${aid}`, data).then((r) => r.data),
  deleteAppointment: (cardId, aid) => apiClient.delete(`/pipeline/cards/${cardId}/appointments/${aid}`).then((r) => r.data),
  // Reminders (fire in-app notifications when due)
  reminders: (cardId) => apiClient.get(`/pipeline/cards/${cardId}/reminders`).then((r) => r.data),
  createReminder: (cardId, data) => apiClient.post(`/pipeline/cards/${cardId}/reminders`, data).then((r) => r.data),
  updateReminder: (cardId, rid, data) => apiClient.patch(`/pipeline/cards/${cardId}/reminders/${rid}`, data).then((r) => r.data),
  deleteReminder: (cardId, rid) => apiClient.delete(`/pipeline/cards/${cardId}/reminders/${rid}`).then((r) => r.data),
  // Emails (inbox conversations matching contactEmail)
  emails: (cardId) => apiClient.get(`/pipeline/cards/${cardId}/emails`).then((r) => r.data),
  // Forms (demo_requests + marketing_leads matching contactEmail)
  forms: (cardId) => apiClient.get(`/pipeline/cards/${cardId}/forms`).then((r) => r.data),
  // Linked-user check (green pill if contact matches a real DB user)
  linkedUser: (cardId) => apiClient.get(`/pipeline/cards/${cardId}/linked-user`).then((r) => r.data),
};

// ----- In-app notifications (bell icon) -----
export const notificationsApi = {
  list: (params = {}) => apiClient.get(`/notifications`, { params }).then((r) => r.data),
  unreadCount: () => apiClient.get(`/notifications/unread-count`).then((r) => r.data),
  markRead: (id) => apiClient.post(`/notifications/${id}/read`).then((r) => r.data),
  markAllRead: () => apiClient.post(`/notifications/read-all`).then((r) => r.data),
};

// ----- Platform Console Inbox (super-admin only). Same shape as inboxApi. -----
export const platformInboxApi = {
  settings: () => apiClient.get(`/platform/inbox/settings`).then((r) => r.data),
  conversations: () => apiClient.get(`/platform/inbox/conversations`).then((r) => r.data),
  getConversation: (id) => apiClient.get(`/platform/inbox/conversations/${id}`).then((r) => r.data),
  messages: (id, params = {}) => apiClient.get(`/platform/inbox/conversations/${id}/messages`, { params }).then((r) => r.data),
  send: (id, data) => apiClient.post(`/platform/inbox/conversations/${id}/send`, data).then((r) => r.data),
  compose: (data) => apiClient.post(`/platform/inbox/compose`, data).then((r) => r.data),
  markRead: (id) => apiClient.post(`/platform/inbox/conversations/${id}/read`).then((r) => r.data),
  unread: () => apiClient.get(`/platform/inbox/unread`).then((r) => r.data),
  simulateInbound: (data) => apiClient.post(`/platform/inbox/simulate-inbound`, data).then((r) => r.data),
  star: (id, important) => apiClient.post(`/platform/inbox/conversations/${id}/star`, { important }).then((r) => r.data),
  deleteConversation: (id) => apiClient.delete(`/platform/inbox/conversations/${id}`).then((r) => r.data),
  deleteMessage: (convId, msgId) => apiClient.delete(`/platform/inbox/conversations/${convId}/messages/${msgId}`).then((r) => r.data),
  trash: () => apiClient.get(`/platform/inbox/trash`).then((r) => r.data),
  restoreConversation: (id) => apiClient.post(`/platform/inbox/conversations/${id}/restore`).then((r) => r.data),
  restoreMessage: (convId, msgId) => apiClient.post(`/platform/inbox/conversations/${convId}/messages/${msgId}/restore`).then((r) => r.data),
  upload: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiClient.post(`/platform/inbox/attachments`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
};

// ----- Voice AI agent -----
export const voiceApi = {
  agent: (blob, { jobId, customerId, durationMs, clientNow, history } = {}) => {
    const fd = new FormData();
    const type = blob.type || "audio/webm";
    const ext = type.includes("mp4") ? "mp4" : type.includes("ogg") ? "ogg" : type.includes("wav") ? "wav" : "webm";
    fd.append("file", blob, `command.${ext}`);
    if (jobId) fd.append("jobId", jobId);
    if (customerId) fd.append("customerId", customerId);
    if (durationMs) fd.append("durationMs", String(durationMs));
    if (clientNow) fd.append("clientNow", clientNow);
    if (history && history.length) fd.append("historyJson", JSON.stringify(history));
    return apiClient.post(`/voice/agent`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  agentText: ({ transcript, jobId, customerId, clientNow, history } = {}) =>
    apiClient.post(`/voice/agent/text`, { transcript, jobId, customerId, clientNow, history }).then((r) => r.data),
  execute: (payload) => apiClient.post(`/voice/execute`, payload).then((r) => r.data),
  tts: (text) => apiClient.post(`/voice/tts`, { text }, { responseType: "blob" }).then((r) => r.data),
};
